import { useState, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, Mail, MapPin } from "lucide-react";
import axios from "axios";

const API = `/api`;

const Footer = () => {
  const [groups, setGroups] = useState([]);
  const currentYear = new Date().getFullYear();
  const location = useLocation();

  const fetchGroups = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/groups`);
      setGroups(res.data);
    } catch {
      // silent
    }
  }, []);

  useEffect(() => { fetchGroups(); }, [fetchGroups]);

  const knownRoutes = ['/', '/products', '/about', '/contact', '/reviews', '/inquiry', '/become-partner', '/login', '/admin'];
  const isKnownRoute = knownRoutes.includes(location.pathname) || location.pathname.startsWith('/products/');
  const showMap = isKnownRoute;

  return (
    <footer className="bg-[#1C1C1A] text-[#F7F5F0]" data-testid="footer">
      {/* Map Section - hidden on 404 pages */}
      {showMap && (
        <div className="w-full h-64 bg-[#2A2A28] relative" data-testid="footer-map">
        <div className="absolute inset-0 flex items-center justify-center text-[#6B6A65] text-sm z-0">Loading map...</div>
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3684.5468372647!2d88.30908127596!3d22.559661633567!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a0279c7e8d0c1a1%3A0x8b0e5a3b1c7d8e9f!2s26%2C%20Jagat%20Banerjee%20Ghat%20Rd%2C%20Choura%20Bustee%2C%20Shibpur%2C%20Howrah%2C%20West%20Bengal%20711102!5e0!3m2!1sen!2sin!4v1699999999999!5m2!1sen!2sin"
          width="100%"
          height="256"
          style={{ border: 0 }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="DUGARS Location"
          className="relative z-10"
        />
      </div>
      )}

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          {/* Brand Column */}
          <div className="md:col-span-4">
            <Link to="/" className="block mb-6" data-testid="footer-logo-link">
              <img src="/api/images/78c1bf13092e4f67a60e7841c49a6c8a.png" alt="DUGARS — Premium Fabric Trading" className="h-14 w-auto brightness-0 invert" />
            </Link>
            <p className="text-[#F7F5F0]/60 font-light leading-relaxed text-sm mb-6">
              Your trusted partner for affordable, quality-assured fabrics. We deliver premium Pique, Herringbone, and Dobby weave fabrics to garment businesses across India.
            </p>
            {/* Social Media Placeholder Icons */}
            <div className="flex gap-4" data-testid="social-links">
              {/* TODO: Replace # with actual social media URLs when available */}
              <a href="#" className="w-9 h-9 border border-[#F7F5F0]/20 flex items-center justify-center hover:border-[#F7F5F0]/50 hover:bg-[#F7F5F0]/10 transition-colors" aria-label="Instagram" data-testid="social-instagram">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>
              </a>
              <a href="#" className="w-9 h-9 border border-[#F7F5F0]/20 flex items-center justify-center hover:border-[#F7F5F0]/50 hover:bg-[#F7F5F0]/10 transition-colors" aria-label="Facebook" data-testid="social-facebook">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>
              </a>
              <a href="#" className="w-9 h-9 border border-[#F7F5F0]/20 flex items-center justify-center hover:border-[#F7F5F0]/50 hover:bg-[#F7F5F0]/10 transition-colors" aria-label="LinkedIn" data-testid="social-linkedin">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/><rect x="2" y="9" width="4" height="12"/><circle cx="4" cy="4" r="2"/></svg>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div className="md:col-span-2">
            <h3 className="text-sm uppercase tracking-widest font-medium text-[#F7F5F0]/80 mb-6">Quick Links</h3>
            <ul className="space-y-3">
              {[
                { to: "/", label: "Home" },
                { to: "/products", label: "Collections" },
                { to: "/about", label: "About Us" },
                { to: "/contact", label: "Contact" },
                { to: "/reviews", label: "Reviews" },
                { to: "/become-partner", label: "Partner With Us" },
              ].map(link => (
                <li key={link.to}>
                  <Link to={link.to} className="text-[#F7F5F0]/50 hover:text-[#F7F5F0] text-sm transition-colors" data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}>{link.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Collections */}
          <div className="md:col-span-3">
            <h3 className="text-sm uppercase tracking-widest font-medium text-[#F7F5F0]/80 mb-6">Collections</h3>
            <ul className="space-y-3">
              {groups.map(g => (
                <li key={g.id}>
                  <Link to={`/products/${g.id}`} className="text-[#F7F5F0]/50 hover:text-[#F7F5F0] text-sm transition-colors" data-testid={`footer-collection-${g.id}`}>{g.name}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div className="md:col-span-3">
            <h3 className="text-sm uppercase tracking-widest font-medium text-[#F7F5F0]/80 mb-6">Contact</h3>
            <ul className="space-y-4">
              <li className="flex gap-3">
                <MapPin size={16} className="text-[#F7F5F0]/40 mt-1 flex-shrink-0" />
                <span className="text-[#F7F5F0]/50 text-sm leading-relaxed">
                  26, Jagat Banerjee Ghat Rd,<br />Choura Bustee, Shibpur,<br />Howrah, West Bengal 711102
                </span>
              </li>
              <li>
                <a href="tel:+916360372623" className="flex items-center gap-3 text-[#F7F5F0]/50 hover:text-[#F7F5F0] text-sm transition-colors" data-testid="footer-phone">
                  <Phone size={16} className="text-[#F7F5F0]/40 flex-shrink-0" />
                  +91 6360372623
                </a>
              </li>
              <li>
                {/* Replace with actual domain email when mail server is configured */}
                <a href="mailto:info@thedugars.in" className="flex items-center gap-3 text-[#F7F5F0]/50 hover:text-[#F7F5F0] text-sm transition-colors" data-testid="footer-email">
                  <Mail size={16} className="text-[#F7F5F0]/40 flex-shrink-0" />
                  info@thedugars.in
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-[#F7F5F0]/10 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-[#F7F5F0]/50 text-sm font-light">
            &copy; {currentYear} DUGARS. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
